import React from 'react';
import PropTypes from 'prop-types';
import TrebbleButton from '../common/TrebbleButton';
import TrebbleCard from '../common/TrebbleCard';
import TrebbleProgress from '../common/Progress';
import TrebbleTable from '../common/Table';
import ModalHeader from '../common/ModalHeader';

/**
 * WorkspaceUsageContent displays workspace usage data including a progress gauge,
 * per-member breakdown table, and a "Buy more minutes" action.
 * Rendered inside an OptionPopupView. Visible to Owner/Admin only.
 *
 * @param {Object} props
 * @param {Object} props.usage - { total, used, remaining, perMember: [{ name, minutesUsed, percentOfTotal }] }
 * @param {function} props.onBuyMore - () => void
 * @param {function} props.onClose - () => void
 * @param {Object} props.i18nModule - i18n module for translations
 */
const WorkspaceUsageContent = ({
    usage,
    onBuyMore,
    onClose,
    i18nModule,
}) => {
    const t = (key) => {
        return window.getI18n(i18nModule, key);
    };

    const formatTemplate = (template, vars) => {
        let result = template;
        Object.keys(vars).forEach((key) => {
            result = result.replace('${' + key + '}', String(vars[key]));
        });
        return result;
    };

    const usagePercent = usage && usage.total > 0
        ? Math.round((usage.used / usage.total) * 100)
        : 0;

    const getProgressStatus = () => {
        if (usagePercent >= 90) return 'exception';
        return 'active';
    };

    const columns = [
        {
            title: t('MEMBER_NAME'),
            dataIndex: 'name',
            key: 'name',
            ellipsis: true,
        },
        {
            title: t('MEMBER_MINUTES_USED'),
            dataIndex: 'minutesUsed',
            key: 'minutesUsed',
            width: 120,
            align: 'right',
        },
        {
            title: t('MEMBER_PERCENT_OF_TOTAL'),
            dataIndex: 'percentOfTotal',
            key: 'percentOfTotal',
            width: 100,
            align: 'right',
            render: (value) => value + '%',
        },
    ];

    const tableData = usage && usage.perMember
        ? usage.perMember.map((member, index) => ({
            key: index,
            name: member.name,
            minutesUsed: member.minutesUsed,
            percentOfTotal: member.percentOfTotal,
        }))
        : [];

    if (!usage) {
        return (
            <TrebbleCard
                useEditorTheme={false}
                style={{ padding: '24px' }}
            >
                <div style={{ textAlign: 'center', color: '#888', padding: '20px 0' }}>
                    {t('NO_USAGE_DATA')}
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
                    <TrebbleButton
                        onClick={onClose}
                        useEditorTheme={false}
                    >
                        {t('CLOSE')}
                    </TrebbleButton>
                </div>
            </TrebbleCard>
        );
    }

    return (
        <TrebbleCard
            useEditorTheme={false}
            style={{ padding: '24px' }}
        >
            <ModalHeader
                title={t('USAGE_TITLE')}
            />

            {/* Usage gauge */}
            <div style={{ marginBottom: '20px' }}>
                <div style={{ fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>
                    {formatTemplate(t('MINUTES_USED'), {
                        used: usage.used,
                        total: usage.total,
                    })}
                </div>
                <TrebbleProgress
                    percent={usagePercent}
                    status={getProgressStatus()}
                    useEditorTheme={false}
                    strokeLinecap="square"
                    style={{ marginBottom: '8px' }}
                />
                <div style={{ fontSize: '13px', color: '#888' }}>
                    {formatTemplate(t('MINUTES_REMAINING'), {
                        remaining: usage.remaining,
                    })}
                </div>
            </div>

            {/* Per-member breakdown */}
            <div style={{ marginBottom: '20px' }}>
                <div style={{ fontSize: '14px', fontWeight: 500, marginBottom: '12px' }}>
                    {t('PER_MEMBER_BREAKDOWN')}
                </div>
                <TrebbleTable
                    columns={columns}
                    dataSource={tableData}
                    pagination={false}
                    size="small"
                    style={{ marginBottom: '4px' }}
                />
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <TrebbleButton
                    onClick={onClose}
                    useEditorTheme={false}
                >
                    {t('CLOSE')}
                </TrebbleButton>
                <TrebbleButton
                    type="primary"
                    onClick={onBuyMore}
                    useEditorTheme={false}
                >
                    {t('BUY_MORE_MINUTES')}
                </TrebbleButton>
            </div>
        </TrebbleCard>
    );
};

WorkspaceUsageContent.propTypes = {
    usage: PropTypes.shape({
        total: PropTypes.number.isRequired,
        used: PropTypes.number.isRequired,
        remaining: PropTypes.number.isRequired,
        perMember: PropTypes.arrayOf(
            PropTypes.shape({
                name: PropTypes.string.isRequired,
                minutesUsed: PropTypes.number.isRequired,
                percentOfTotal: PropTypes.number.isRequired,
            })
        ),
    }),
    onBuyMore: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    i18nModule: PropTypes.object.isRequired,
};

export { WorkspaceUsageContent };
export default WorkspaceUsageContent;
